import {
	Button,
	Empty,
	Input,
	Modal,
	Radio,
	Space,
	Spin,
	Tag,
	Typography,
	message,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';

import {
	CertOrderBrief,
	CertProposal,
	useAssignCertProposalMutation,
	useGetCertProposalLineItemsQuery,
	useSearchCertPosQuery,
} from '../../redux/api/material-certs';
import SiteTag from './SiteTag';

const { Text } = Typography;

const describe = (item: {
	type_name: string | null;
	shape_name: string | null;
	vendor_part_number: string | null;
	height: string | null;
	width: string | null;
	outer_diameter: string | null;
	length: string | null;
}) => {
	const size = item.outer_diameter
		? `${item.outer_diameter} OD`
		: [item.height, item.width].filter(Boolean).join(' x ');
	return [item.type_name, item.shape_name, size, item.length ? `${item.length}"` : null]
		.filter(Boolean)
		.join(' · ');
};

const orderLabel = (order: CertOrderBrief) =>
	order.purchase_order_name || order.purchase_order_number || 'Unnamed PO';

interface Props {
	proposal: CertProposal;
	open: boolean;
	onClose: () => void;
}

/**
 * Pick the ERP line by hand when the matcher guessed wrong or gave up.
 *
 * Two levels, because either can be wrong. When the PO resolved, the reviewer
 * only picks a line. When it did not -- a mistype on either side, or a PO
 * raised after the certificate arrived -- they search for the PO first, since
 * the vendor's numbering and ours are not linked.
 *
 * Using a line from here records the choice as a manual match, which later
 * rematches leave alone. Match on the heat still attaches.
 */
export default function ReassignModal({ proposal, open, onClose }: Props) {
	const [searching, setSearching] = useState(!proposal.material_order);
	const [term, setTerm] = useState(proposal.po || '');
	const [debounced, setDebounced] = useState(term);
	const [pickedOrder, setPickedOrder] = useState<CertOrderBrief | null>(null);
	const [choice, setChoice] = useState<string | null>(
		proposal.material_order_line_item
	);

	useEffect(() => {
		const timer = setTimeout(() => setDebounced(term.trim()), 300);
		return () => clearTimeout(timer);
	}, [term]);

	const { data: orders, isFetching: findingOrders } = useSearchCertPosQuery(
		debounced,
		{ skip: !open || !searching || debounced.length < 2 }
	);

	const orderId = pickedOrder?.id ?? null;
	const { data: lineItems, isFetching } = useGetCertProposalLineItemsQuery(
		{ id: proposal.id, materialOrderId: orderId },
		{ skip: !open || (searching && !orderId) }
	);

	const [assign, { isLoading }] = useAssignCertProposalMutation();

	const title = useMemo(() => {
		const what =
			proposal.vendor_key === 'mcmaster'
				? `line ${proposal.line_number ?? '?'}`
				: proposal.heat
					? `HT# ${proposal.heat}`
					: 'this cert';
		if (searching && !pickedOrder) {
			return `Find the PO for ${what}`;
		}
		const on = pickedOrder ? orderLabel(pickedOrder) : proposal.po;
		return `Pick a line for ${what} on ${on}`;
	}, [
		searching,
		pickedOrder,
		proposal.heat,
		proposal.po,
		proposal.vendor_key,
		proposal.line_number,
	]);

	const submit = async () => {
		if (!choice) return;
		try {
			await assign({
				id: proposal.id,
				material_order_line_item_id: choice,
			}).unwrap();
			message.success('Line set. Match when it looks right.');
			onClose();
		} catch (error) {
			const detail = (error as { data?: { detail?: string } })?.data?.detail;
			message.error(detail || 'Could not set the line.');
		}
	};

	const pickingLines = !searching || Boolean(pickedOrder);

	return (
		<Modal
			open={open}
			title={title}
			onCancel={onClose}
			onOk={submit}
			okText="Use this line"
			okButtonProps={{ disabled: !choice, loading: isLoading }}
			width={620}
		>
			{searching && !pickedOrder ? (
				<>
					<Text type="secondary">
						{proposal.vendor_key === 'mcmaster' ? 'McMaster' : 'Alro'} quotes PO{' '}
						{proposal.po || '(none)'}. Search by either number —
						ERP may store it as {proposal.po ? `PO-${proposal.po}` : 'PO-nnnn'}.
					</Text>
					<Input.Search
						autoFocus
						value={term}
						placeholder="PO number"
						onChange={(event) => setTerm(event.target.value)}
						style={{ marginTop: 12 }}
					/>
					{findingOrders ? (
						<Spin style={{ marginTop: 16 }} />
					) : !orders?.length ? (
						<Empty
							style={{ marginTop: 16 }}
							description={
								debounced.length < 2
									? 'Type at least two characters.'
									: 'No purchase order matches.'
							}
						/>
					) : (
						<Space direction="vertical" style={{ marginTop: 16, width: '100%' }}>
							{orders.map((order) => (
								<Button
									key={order.id}
									block
									style={{ textAlign: 'left', height: 'auto', padding: 8 }}
									onClick={() => {
										setPickedOrder(order);
										setChoice(null);
									}}
								>
									<Space>
										<Text strong>{orderLabel(order)}</Text>
										{order.vendor_name && <Tag>{order.vendor_name}</Tag>}
										{/* Shown while choosing, because picking a PO from
										    the wrong plant is the mistake this search makes
										    easiest to commit. */}
										<SiteTag name={order.building_name} compact />
										{order.status && (
											<Text type="secondary">{order.status}</Text>
										)}
										<Text type="secondary">
											{order.line_item_count} line
											{order.line_item_count === 1 ? '' : 's'}
										</Text>
									</Space>
								</Button>
							))}
						</Space>
					)}
				</>
			) : null}

			{pickingLines ? (
				<>
					{isFetching ? (
						<Spin />
					) : !lineItems?.length ? (
						<Empty description="This PO has no lines to choose from." />
					) : (
						<Radio.Group
							value={choice}
							onChange={(event) => setChoice(event.target.value)}
						>
							<Space direction="vertical">
								{lineItems.map((item) => (
									<Radio key={item.id} value={item.id}>
										{describe(item)}
										{item.vendor_part_number && (
											<Text type="secondary">
												{' '}
												(EDP# {item.vendor_part_number})
											</Text>
										)}
									</Radio>
								))}
							</Space>
						</Radio.Group>
					)}
					<div style={{ marginTop: 16 }}>
						<Button
							type="link"
							style={{ paddingLeft: 0 }}
							onClick={() => {
								setSearching(true);
								setPickedOrder(null);
								setChoice(null);
							}}
						>
							Pick a different PO
						</Button>
					</div>
				</>
			) : null}
		</Modal>
	);
}
